import { createTableColumn, SelectionItemId, Table, TableCell, TableColumnDefinition, TableColumnSizingOptions, TableHeader, TableHeaderCell, TableRow, TableRowId, TableSelectionCell, useTableColumnSizing_unstable, useTableFeatures, useTableSelection } from "@fluentui/react-components";
import React from "react";
import { useLoading } from "../../../utils/loading-indicator.component";
import { TableBodyWithLoading } from "s365-dashboard-v2-file-picker";
import ShowLocalTime from "../../../components/show-local-time/show-local-time.component";
import { FileTableItem } from "../../models/file-table.models";
import { _copyAndSort } from "../../../utils/helpers/array.helpers";
import { processServerError } from "../../../utils/helpers/error.helper";
import { ApplicationSelectionModel } from "./copy-file-objects-dialog.component";
import { ExperimentForListResponseModel } from "../../../swagger-clients/ai-for-pfd-clients.service";
import { getExperimentsClient } from "../../../services/ai-for-pfd.service";
import { ExperimentStatusComponent } from "../../../components/experiment-status/experiment-status.component";

type SelectCopilotExperimentsTableProps = {
    file: FileTableItem;
    //can use this to show number of items that are loaded
    onDataLoaded: (count: number) => void;
    onSelectedItemsChanged: (selectedItems: ApplicationSelectionModel[]) => void;
    selectedItems: number[];
}



export const SelectCopilotExperimentsTable: React.FC<SelectCopilotExperimentsTableProps> = (props) => {
    const [items, setItems] = React.useState<ExperimentForListResponseModel[]>([]);
    const [isLoading, loadingService] = useLoading();
    const [columns, setColumns] = React.useState<TableColumnDefinition<ExperimentForListResponseModel>[]>([]);
    const [selectedRows, setSelectedRows] = React.useState(
        () => new Set<TableRowId>(props.selectedItems)
    );
    React.useEffect(() => { getExperiments(); }, []);


    const [columnSizingOptions] = React.useState<TableColumnSizingOptions>({
        checkbox: {
            minWidth: 30,
            idealWidth: 30,
            autoFitColumns: false
        },
        id: {
            minWidth: 70,
            defaultWidth:70,
            idealWidth: 70,
            autoFitColumns: false
        },
        name: {
            minWidth: 100,
            defaultWidth: 9000,
        },
        processingStatus: {
            minWidth: 200,
            defaultWidth: 200
        },
        created: {
            minWidth: 90,
            defaultWidth: 200
        }
    });


    const { getRows, columnSizing_unstable, tableRef, selection: {
        toggleRow,
        selectRow,
        clearRows,
        toggleAllRows
    }, } = useTableFeatures<ExperimentForListResponseModel>(
        {
            columns,
            items,
            getRowId: (item: ExperimentForListResponseModel) => (+item.id)

        },
        [useTableColumnSizing_unstable({ columnSizingOptions }),
        useTableSelection({
            selectionMode: "multiselect",
            selectedItems: selectedRows,
            onSelectionChange: (e, data) => {
                setSelectedRows(new Set<TableRowId>(data.selectedItems));
                const selectedIds = [...data.selectedItems].map(x => +x);
                const mappedSelections = selectedIds?.map(id => {
                    const experiment = items.find(x => x.id == id);
                    return { id: +experiment.id } as ApplicationSelectionModel;
                });
                props.onSelectedItemsChanged(mappedSelections);
            },
        })
        ]
    );
    const isRowSelected = (rowId: number) => {
        return selectedRows.has(rowId);
    }

    const rows = getRows((row) => {
        const selected = isRowSelected(+row.item.id);
        return {
            ...row,
            rowId: +row.item.id,
            selected
        };
    });
    React.useEffect(() => {
        const columnsLocal = getAllColumns();
        setColumns(columnsLocal);
    }, [selectedRows, items]);

    const getAllColumns = () => {

        const allRowsSelected = items.length > 0 && [...selectedRows].length > 0 && [...selectedRows].length == items.length;
        const someRowsSelected = items.length > 0 && [...selectedRows].length > 0 && !allRowsSelected;

        let columnsLocal: TableColumnDefinition<ExperimentForListResponseModel>[] = [
            createTableColumn<ExperimentForListResponseModel>({
                columnId: "checkbox",
                renderHeaderCell: () => {
                    return <TableSelectionCell
                        checked={
                            allRowsSelected ? true : someRowsSelected ? "mixed" : false
                        }
                        onClick={(ev) => onToggleAllRows(ev)}
                        checkboxIndicator={{ "aria-label": "Select all rows " }}
                    />
                },
                renderCell: (item) => {
                    const selected = isRowSelected(+item.id);

                    return <TableSelectionCell

                        checked={selected}
                        onClick={(ev) => { ev.stopPropagation(); toggleRow(ev, +item.id); }}
                        {...columnSizing_unstable.getTableCellProps("checkbox")}
                    />
                }
            }),
            createTableColumn<ExperimentForListResponseModel>({
                columnId: "id",
                renderHeaderCell: () => <>ID</>,
                renderCell: (item: ExperimentForListResponseModel) => {
                    return <span>{item.id}</span>;

                }
            }),
            createTableColumn<ExperimentForListResponseModel>({
                columnId: "name",
                renderHeaderCell: () => <>Name</>,
                renderCell: (item: ExperimentForListResponseModel) => {
                    return <span>{item.name}</span>;

                }
            }),
            createTableColumn<ExperimentForListResponseModel>({
                columnId: "processingStatus",
                renderHeaderCell: () => <>Processing Status</>,
                renderCell: (item: ExperimentForListResponseModel) => {
                    return <ExperimentStatusComponent status={item.processingStatus} />;
                }
            }),
            createTableColumn<ExperimentForListResponseModel>({
                columnId: "created",
                renderHeaderCell: () => <>Created</>,
                renderCell: (item: ExperimentForListResponseModel) => {

                    return <ShowLocalTime date={item.createdAt} />;
                }
            })
        ];

        return columnsLocal;
    }



    const isColumnCentered = (columnId: string) => {

        switch (columnId) {
            case "processingStatus": return true;
            case "created": return true;

            default: return false;
        }
    }


    const getExperiments = async () => {
        loadingService.showLoading("Loading...", async (hideMessage) => {
            try {
                const client = getExperimentsClient();

                const experimentsResp = await client.getExperiments(props.file.uniqueIdentifier);

                let experimentsSorted = _copyAndSort<ExperimentForListResponseModel>(experimentsResp, "createdAt", true);
                experimentsSorted = experimentsSorted.filter(x => !x.isDeleted);

                setItems(experimentsSorted ?? []);
                props.onDataLoaded(experimentsSorted?.length ?? 0);

            } catch (error) {
                processServerError(error, undefined, "An error occurred while getting copilot experiments.");
            } finally {
                hideMessage();
            }
        });

    }
    const onToggleAllRows = (ev: any) => {
        toggleAllRows(ev);
    }

    return <div>
        <div className='scenarios-table__wrapper'>
            <Table ref={tableRef} as="table" {...columnSizing_unstable.getTableProps()} >
                <TableHeader>
                    <TableRow>
                        {columns.map((column) => (
                            <TableHeaderCell
                                onDragStart={e => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                }}
                                className={`${isColumnCentered(column.columnId as string) ? 'files-table__cell--center' : ''} files-table__cell--bold`}
                                {...columnSizing_unstable.getTableHeaderCellProps(
                                    column.columnId
                                )}
                            >
                                {column.renderHeaderCell()}
                            </TableHeaderCell>
                        ))}
                    </TableRow>
                </TableHeader>
                <TableBodyWithLoading isLoading={isLoading}
                    columnCount={5} loadingMessage="Loading..."
                    itemCount={items ? items.length : 0}
                    noItemsMessage="No items found.">
                    {!isLoading && rows.map(({ item, selected }) => (
                        <TableRow
                            key={`scenario-${item.id}`}
                            style={{ cursor: "pointer" }}
                            onClick={() => { toggleRow(null, +item.id); }}
                        >

                            {columns.map((column) => (
                                <TableCell
                                    className={`${isColumnCentered(column.columnId as string) ? ' column--center' : ''}`}
                                    {...columnSizing_unstable.getTableCellProps(column.columnId)}
                                >
                                    {column.renderCell(item)}
                                </TableCell>
                            ))}

                        </TableRow>
                    ))}


                </TableBodyWithLoading>
            </Table>
        </div>
    </div>
}

